import supabase from './supabase';

/**
 * Google OAuth and Platform Connection Management Service
 * Handles Google Business Profile OAuth flow and connection management
 */
class GoogleAuthService {
  /**
   * Initiate OAuth flow - redirects user to Google consent screen
   * @param {string} organizationId - Organization ID to connect
   */
  initiateOAuthFlow(organizationId) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      throw new Error('Google OAuth client ID not configured');
    }

    // OAuth parameters
    const redirectUri = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-oauth-callback`;
    // Request business.manage for reviews + userinfo scopes for account details
    const scopes = [
      'https://www.googleapis.com/auth/business.manage',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/userinfo.profile',
    ].join(' ');
    const state = encodeURIComponent(JSON.stringify({ organizationId }));

    // Build Google OAuth URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', scopes);
    authUrl.searchParams.append('access_type', 'offline'); // Get refresh token
    authUrl.searchParams.append('prompt', 'consent'); // Force consent to ensure refresh token
    authUrl.searchParams.append('state', state);

    // Redirect to Google
    window.location.href = authUrl.toString();
  }

  /**
   * Get Google connection for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object|null>} Connection object or null if not found
   */
  async getConnection(organizationId) {
    try {
      const { data, error } = await supabase
        .from('platform_connections')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('platform', 'google')
        .maybeSingle();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error getting Google connection:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to get connection',
        data: null
      };
    }
  }

  /**
   * Check if organization has an active Google connection
   * @param {string} organizationId - Organization ID
   * @returns {Promise<boolean>} True if connected
   */
  async isConnected(organizationId) {
    const result = await this.getConnection(organizationId);
    return result.success && result.data && result.data.is_active;
  }

  /**
   * Disconnect Google Business Profile from organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Result object
   */
  async disconnect(organizationId) {
    try {
      const { error } = await supabase
        .from('platform_connections')
        .delete()
        .eq('organization_id', organizationId)
        .eq('platform', 'google');

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error disconnecting Google:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to disconnect'
      };
    }
  }

  /**
   * Update connection status
   * @param {string} organizationId - Organization ID
   * @param {boolean} isActive - Active status
   * @returns {Promise<Object>} Result object
   */
  async updateConnectionStatus(organizationId, isActive) {
    try {
      const { error } = await supabase
        .from('platform_connections')
        .update({ 
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('organization_id', organizationId)
        .eq('platform', 'google');

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error updating connection status:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to update connection'
      };
    }
  }

  /**
   * Get sync logs for an organization
   * @param {string} organizationId - Organization ID
   * @param {number} limit - Number of logs to fetch
   * @returns {Promise<Object>} Sync logs
   */
  async getSyncLogs(organizationId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('platform_sync_logs')
        .select('*')
        .eq('organization_id', organizationId)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error getting sync logs:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to get sync logs',
        data: []
      };
    }
  }

  /**
   * Get the last successful sync timestamp
   * @param {string} organizationId - Organization ID
   * @returns {Promise<string|null>} ISO timestamp or null
   */
  async getLastSyncTime(organizationId) {
    try {
      const connection = await this.getConnection(organizationId);
      if (!connection.success || !connection.data) {
        return null;
      }
      return connection.data.last_sync_at;
    } catch (error) {
      console.error('Error getting last sync time:', error);
      return null;
    }
  }
}

export default new GoogleAuthService();

